import React from 'react';
import MaterialTable from 'material-table';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  IconButton,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import AddIcon from '@material-ui/icons/Add';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import GetAppIcon from '@material-ui/icons/GetApp';
import DescriptionIcon from '@material-ui/icons/Description';
import SearchIcon from '@material-ui/icons/Search';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import CloseIcon from '@material-ui/icons/Close';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';

const csrfToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') : '';
};

const formatBytes = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default class HerminaRunBenefitsTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      statusFilter: 'all',
      searchQuery: '',
      searchInput: '',
      pageSize: 10,
      lastMeta: {},
      addDialogOpen: false,
      addCode: '',
      addExpiresAt: '',
      addErrors: [],
      adding: false,
      uploadDialogOpen: false,
      uploadFile: null,
      uploading: false,
      uploadResult: null,
      dragActive: false,
      exportMenuAnchor: null,
      templateMenuAnchor: null,
      snackbarOpen: false,
      snackbarMessage: '',
    };
    this.tableRef = React.createRef();
    this.searchTimer = null;
  }

  componentWillUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  get pageLimit() {
    return Number(this.props.limit) > 0 ? Number(this.props.limit) : 10;
  }

  get isWidget() {
    return Number(this.props.limit) > 0;
  }

  refreshTable = () => {
    if (this.tableRef.current) this.tableRef.current.onQueryChange();
  };

  handleStatusFilterChange = (event) => {
    this.setState({ statusFilter: event.target.value }, this.refreshTable);
  };

  handleSearchChange = (event) => {
    const value = event.target.value;
    this.setState({ searchInput: value });
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.setState({ searchQuery: value.trim() }, this.refreshTable);
    }, 350);
  };

  clearSearch = () => {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.setState({ searchInput: '', searchQuery: '' }, this.refreshTable);
  };

  openSnackbar = (message) => this.setState({ snackbarOpen: true, snackbarMessage: message });
  closeSnackbar = () => this.setState({ snackbarOpen: false, snackbarMessage: '' });

  // --- Add ---
  openAddDialog = () => this.setState({ addDialogOpen: true, addCode: '', addExpiresAt: '', addErrors: [] });
  closeAddDialog = () => this.setState({ addDialogOpen: false, addErrors: [] });

  submitAdd = () => {
    const { addCode, addExpiresAt } = this.state;
    this.setState({ adding: true, addErrors: [] });
    fetch(this.props.api_url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken(),
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ benefit: { unique_code: addCode, expires_at: addExpiresAt } }),
    })
      .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
      .then(({ ok, body }) => {
        if (ok) {
          this.setState({ adding: false, addDialogOpen: false });
          this.openSnackbar(body.message || 'Saved');
          this.refreshTable();
        } else {
          const errors = Array.isArray(body.errors) ? body.errors : [body.message || body.errors || 'Failed to save'];
          this.setState({ adding: false, addErrors: errors.filter(Boolean) });
        }
      })
      .catch(() => {
        this.setState({ adding: false, addErrors: ['Network error'] });
      });
  };

  // --- Delete (material-table action) ---
  deleteRow = (row) => new Promise((resolve, reject) => {
    const url = row.destroy_path;
    if (!url) return reject();
    const confirmMsg = this.props.labels.delete_confirm;
    if (!window.confirm(confirmMsg)) return reject();
    fetch(url, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrfToken(), 'Accept': 'application/json' },
    })
      .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
      .then(({ ok, body }) => {
        this.openSnackbar(body.message || (ok ? 'Deleted' : 'Failed'));
        if (ok) {
          this.refreshTable();
          resolve();
        } else {
          reject();
        }
      })
      .catch(() => { this.openSnackbar('Network error'); reject(); });
  });

  // --- Update (material-table inline edit) ---
  updateRow = (newData) => new Promise((resolve, reject) => {
    const url = newData.update_path;
    if (!url) return reject();
    const code = String(newData.unique_code || '').toUpperCase();
    const expiresAt = String(newData.expires_at || '').slice(0, 10);
    if (!/^[A-Z0-9]{6}$/.test(code) || !expiresAt) {
      this.openSnackbar(this.props.labels.unique_code_hint);
      return reject();
    }
    fetch(url, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': csrfToken(),
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ benefit: { unique_code: code, expires_at: expiresAt } }),
    })
      .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
      .then(({ ok, body }) => {
        if (ok) {
          this.openSnackbar(body.message || 'Updated');
          this.refreshTable();
          resolve();
        } else {
          const msg = Array.isArray(body.errors) ? body.errors.join(', ') : (body.errors || body.message || 'Failed');
          this.openSnackbar(msg);
          reject();
        }
      })
      .catch(() => { this.openSnackbar('Network error'); reject(); });
  });

  // --- Upload ---
  openUploadDialog = () => this.setState({ uploadDialogOpen: true, uploadFile: null, uploadResult: null, dragActive: false });
  closeUploadDialog = () => this.setState({ uploadDialogOpen: false, uploadFile: null, uploadResult: null, dragActive: false });

  handleFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    this.setState({ uploadFile: file || null, uploadResult: null });
  };

  handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files && event.dataTransfer.files[0];
    this.setState({ uploadFile: file || null, dragActive: false, uploadResult: null });
  };

  handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!this.state.dragActive) this.setState({ dragActive: true });
  };

  handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ dragActive: false });
  };

  clearSelectedFile = () => this.setState({ uploadFile: null, uploadResult: null });

  submitUpload = () => {
    if (!this.state.uploadFile) return;
    this.setState({ uploading: true, uploadResult: null });
    const formData = new FormData();
    formData.append('file', this.state.uploadFile);
    fetch(this.props.bulk_upload_url, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken(), 'Accept': 'application/json' },
      body: formData,
    })
      .then((response) => response.json().then((body) => ({ ok: response.ok, body })))
      .then(({ ok, body }) => {
        this.setState({ uploading: false, uploadResult: body });
        if (ok) {
          this.refreshTable();
          this.openSnackbar(body.message || 'Uploaded');
        }
      })
      .catch(() => this.setState({ uploading: false, uploadResult: { success: false, errors: 'Network error' } }));
  };

  // --- Export menu ---
  openExportMenu = (event) => this.setState({ exportMenuAnchor: event.currentTarget });
  closeExportMenu = () => this.setState({ exportMenuAnchor: null });
  triggerDownload = (formatType) => {
    this.closeExportMenu();
    window.location.href = `${this.props.download_url}?format_type=${formatType}`;
  };
  openTemplateMenu = (event) => this.setState({ templateMenuAnchor: event.currentTarget });
  closeTemplateMenu = () => this.setState({ templateMenuAnchor: null });
  triggerTemplateDownload = (formatType) => {
    this.closeTemplateMenu();
    const urls = this.props.template_urls || {};
    const url = urls[formatType] || this.props.template_url;
    if (url) window.location.href = url;
  };

  // --- Columns ---
  columns = () => {
    const labels = this.props.labels;
    return [
      {
        title: labels.code,
        field: 'unique_code',
        cellStyle: { padding: '12px 24px' },
        render: (row) => (
          <span className="benefit-codes-card__code-pill">{row.unique_code}</span>
        ),
        editComponent: (props) => (
          <TextField
            autoFocus
            size="small"
            variant="outlined"
            value={props.value || ''}
            onChange={(e) => props.onChange(e.target.value.toUpperCase().slice(0, 6))}
            inputProps={{ maxLength: 6, style: { fontFamily: 'monospace', letterSpacing: '1px' } }}
          />
        ),
        validate: (row) => (
          /^[A-Z0-9]{6}$/.test(String(row.unique_code || '').toUpperCase())
            ? true
            : { isValid: false, helperText: labels.unique_code_hint }
        ),
      },
      {
        title: labels.status,
        field: 'status',
        editable: 'never',
        cellStyle: { padding: '12px 24px' },
        render: (row) => {
          const isExpired = row.expired && row.status === 'available';
          const key = isExpired ? 'expired' : row.status;
          const label = isExpired
            ? labels.status_expired
            : (row.status === 'claimed' ? labels.status_claimed : labels.status_available);
          return <span className={`benefit-codes-card__status-pill benefit-codes-card__status-pill--${key}`}>{label}</span>;
        },
      },
      {
        title: labels.claimed_by,
        field: 'claimed_by',
        editable: 'never',
        cellStyle: { padding: '12px 24px' },
        render: (row) => row.claimed_by
          ? <span>{row.claimed_by}</span>
          : <span className="benefit-codes-card__empty-cell">—</span>,
      },
      {
        title: labels.claimed_at,
        field: 'claimed_at_formatted',
        editable: 'never',
        cellStyle: { padding: '12px 24px' },
        render: (row) => row.claimed_at_formatted
          ? <span>{row.claimed_at_formatted}</span>
          : <span className="benefit-codes-card__empty-cell">—</span>,
      },
      {
        title: labels.expires_at,
        field: 'expires_at',
        cellStyle: { padding: '12px 24px' },
        render: (row) => row.expires_at_formatted
          ? <span>{row.expires_at_formatted}</span>
          : <span className="benefit-codes-card__empty-cell">—</span>,
        editComponent: (props) => (
          <TextField
            type="date"
            size="small"
            variant="outlined"
            value={String(props.value || '').slice(0, 10)}
            onChange={(e) => props.onChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        ),
        validate: (row) => (row.expires_at ? true : { isValid: false, helperText: labels.expires_at_label }),
      },
    ];
  };

  getTheme = () => {
    const isDarkMode = document.documentElement.getAttribute('data-mode-theme') === 'dark';
    return createTheme({
      palette: {
        type: isDarkMode ? 'dark' : 'light',
        primary: { main: '#2e7d38' },
        secondary: { main: '#41924E' },
        background: {
          paper: isDarkMode ? '#2a3441' : '#ffffff',
          default: isDarkMode ? '#1e2530' : '#f7f9fc',
        },
      },
      overrides: {
        MuiTableCell: { root: { borderBottom: '1px solid #f0f2f6' } },
      },
    });
  };

  fetchData = (query) => new Promise((resolve) => {
    const perPage = this.isWidget ? this.pageLimit : (this.state.pageSize || query.pageSize);
    let url = this.props.api_url;
    url += url.includes('?') ? '&' : '?';
    url += `per_page=${perPage}`;
    url += `&page=${this.isWidget ? 1 : (query.page + 1)}`;
    if (this.state.statusFilter !== 'all') {
      url += `&status=${encodeURIComponent(this.state.statusFilter)}`;
    }
    if (this.state.searchQuery) {
      url += `&q=${encodeURIComponent(this.state.searchQuery)}`;
    }
    fetch(url, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((body) => {
        const data = Array.isArray(body.data) ? body.data : [];
        const meta = body.meta || {};
        this.setState({ lastMeta: meta });
        resolve({
          data,
          page: (meta.page || 1) - 1,
          totalCount: meta.total || 0,
        });
      })
      .catch(() => resolve({ data: [], page: 0, totalCount: 0 }));
  });

  renderToolbar = () => {
    const labels = this.props.labels;
    const meta = this.state.lastMeta || {};
    return (
      <div className="benefit-codes-card__header">
        <div className="benefit-codes-card__title-row">
          <h5 className="benefit-codes-card__title">{labels.title}</h5>
          <span className="benefit-codes-card__counter">
            {labels.counter_summary
              .replace('%{claimed}', meta.claimed_count ?? 0)
              .replace('%{total}', meta.total ?? 0)}
          </span>
        </div>
        <div className="benefit-codes-card__actions">
          {!this.isWidget && (
            <div className="benefit-codes-card__search">
              <SearchIcon className="benefit-codes-card__search-icon" fontSize="small" />
              <input
                type="text"
                className="benefit-codes-card__search-input"
                placeholder={labels.search_placeholder}
                value={this.state.searchInput}
                onChange={this.handleSearchChange}
              />
              {this.state.searchInput && (
                <button
                  type="button"
                  className="benefit-codes-card__search-clear"
                  onClick={this.clearSearch}
                  aria-label="Clear search"
                >
                  <CloseIcon fontSize="small" />
                </button>
              )}
            </div>
          )}
          {!this.isWidget && (
            <FormControl size="small" variant="outlined" className="benefit-codes-card__filter">
              <Select
                value={this.state.statusFilter}
                onChange={this.handleStatusFilterChange}
                displayEmpty
              >
                <MenuItem value="all">{labels.filter_all}</MenuItem>
                <MenuItem value="available">{labels.filter_available}</MenuItem>
                <MenuItem value="claimed">{labels.filter_claimed}</MenuItem>
              </Select>
            </FormControl>
          )}
          <button
            type="button"
            className="benefit-codes-card__btn benefit-codes-card__btn--icon"
            onClick={this.refreshTable}
            aria-label={labels.refresh}
            title={labels.refresh}
          >
            <RefreshIcon fontSize="small" />
          </button>
          <button type="button" className="benefit-codes-card__btn" onClick={this.openExportMenu}>
            <SaveAltIcon fontSize="small" />
            <span>{labels.download_table}</span>
          </button>
          <button type="button" className="benefit-codes-card__btn" onClick={this.openTemplateMenu}>
            <DescriptionIcon fontSize="small" />
            <span>{labels.download_template}</span>
          </button>
          <button type="button" className="benefit-codes-card__btn" onClick={this.openUploadDialog}>
            <CloudUploadIcon fontSize="small" />
            <span>{labels.upload_code}</span>
          </button>
          <button type="button" className="benefit-codes-card__btn benefit-codes-card__btn--primary" onClick={this.openAddDialog}>
            <AddIcon fontSize="small" />
            <span>{labels.add_new}</span>
          </button>
        </div>
      </div>
    );
  };

  renderFooter = () => {
    const labels = this.props.labels;
    const meta = this.state.lastMeta || {};
    if (!this.isWidget) return null;
    if (!this.props.view_all_url) return null;

    return (
      <div className="benefit-codes-card__footer">
        <a href={this.props.view_all_url} className="benefit-codes-card__view-all">
          <VisibilityOutlinedIcon fontSize="small" />
          <span>{labels.view_all_with_count.replace('%{count}', meta.total ?? 0)}</span>
        </a>
      </div>
    );
  };

  render() {
    const labels = this.props.labels;
    const uploadResult = this.state.uploadResult;
    const uploadData = uploadResult && uploadResult.data ? uploadResult.data : null;

    return (
      <MuiThemeProvider theme={this.getTheme()}>
        <div className="benefit-codes-card">
          {this.renderToolbar()}

          <div className="benefit-codes-card__table-wrap">
            <MaterialTable
              tableRef={this.tableRef}
              title=""
              columns={this.columns()}
              data={this.fetchData}
              icons={{
                Edit: React.forwardRef((p, ref) => <EditOutlinedIcon {...p} ref={ref} fontSize="small" />),
                Check: React.forwardRef((p, ref) => <SaveOutlinedIcon {...p} ref={ref} fontSize="small" style={{ color: '#2e7d38' }} />),
                Clear: React.forwardRef((p, ref) => <ClearOutlinedIcon {...p} ref={ref} fontSize="small" />),
                Delete: React.forwardRef((p, ref) => <DeleteOutlineIcon {...p} ref={ref} fontSize="small" />),
                ResetSearch: React.forwardRef((p, ref) => <ClearOutlinedIcon {...p} ref={ref} fontSize="small" />),
              }}
              editable={{
                isEditable: (row) => row.status === 'available',
                isDeletable: (row) => row.status === 'available',
                onRowUpdate: (newData) => this.updateRow(newData),
                onRowDelete: (oldData) => this.deleteRow(oldData),
              }}
              localization={{
                header: { actions: labels.actions },
                body: {
                  emptyDataSourceMessage: labels.empty,
                  editRow: {
                    deleteText: labels.delete_confirm,
                    saveTooltip: labels.save,
                    cancelTooltip: labels.cancel,
                  },
                  editTooltip: labels.edit || 'Edit',
                  deleteTooltip: labels.delete,
                },
                pagination: { labelRowsSelect: 'rows' },
              }}
              onChangeRowsPerPage={(size) => this.setState({ pageSize: size }, this.refreshTable)}
              options={{
                pageSize: this.isWidget ? this.pageLimit : this.state.pageSize,
                pageSizeOptions: this.isWidget ? [this.pageLimit] : [10, 25, 50],
                search: false,
                sorting: false,
                toolbar: false,
                paging: !this.isWidget,
                padding: 'dense',
                actionsColumnIndex: -1,
                headerStyle: {
                  backgroundColor: '#f7f9fc',
                  color: '#6b7280',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 24px',
                  borderTop: '1px solid #edf0f5',
                  borderBottom: '1px solid #edf0f5',
                },
                rowStyle: { fontSize: '0.88rem' },
              }}
              style={{ boxShadow: 'none', background: 'transparent' }}
            />
          </div>

          {this.renderFooter()}
        </div>

        <Menu
          anchorEl={this.state.exportMenuAnchor}
          open={Boolean(this.state.exportMenuAnchor)}
          onClose={this.closeExportMenu}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => this.triggerDownload('xlsx')}>
            <ListItemIcon style={{ minWidth: 36 }}><GetAppIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={labels.download_xlsx} />
          </MenuItem>
          <MenuItem onClick={() => this.triggerDownload('csv')}>
            <ListItemIcon style={{ minWidth: 36 }}><GetAppIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={labels.download_csv} />
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={this.state.templateMenuAnchor}
          open={Boolean(this.state.templateMenuAnchor)}
          onClose={this.closeTemplateMenu}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => this.triggerTemplateDownload('xlsx')}>
            <ListItemIcon style={{ minWidth: 36 }}><DescriptionIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={labels.download_xlsx} />
          </MenuItem>
          <MenuItem onClick={() => this.triggerTemplateDownload('csv')}>
            <ListItemIcon style={{ minWidth: 36 }}><DescriptionIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={labels.download_csv} />
          </MenuItem>
        </Menu>

        {/* Add dialog */}
        <Dialog open={this.state.addDialogOpen} onClose={this.closeAddDialog} maxWidth="xs" fullWidth>
          <DialogTitle>{labels.new_title}</DialogTitle>
          <DialogContent>
            {this.state.addErrors.length > 0 && (
              <Box style={{ color: '#c23c3c', marginBottom: 12, fontSize: '0.85rem' }}>
                {this.state.addErrors.map((err, idx) => (<div key={idx}>{err}</div>))}
              </Box>
            )}
            <TextField
              autoFocus
              fullWidth
              label={labels.unique_code_label}
              placeholder={labels.unique_code_placeholder}
              helperText={labels.unique_code_hint}
              variant="outlined"
              size="small"
              value={this.state.addCode}
              onChange={(e) => this.setState({ addCode: e.target.value.toUpperCase() })}
              inputProps={{ maxLength: 6, style: { fontFamily: 'monospace', letterSpacing: '1px' } }}
              style={{ marginBottom: 16 }}
            />
            <TextField
              fullWidth
              type="date"
              label={labels.expires_at_label}
              variant="outlined"
              size="small"
              value={this.state.addExpiresAt}
              onChange={(e) => this.setState({ addExpiresAt: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeAddDialog} size="small">{labels.cancel}</Button>
            <Button
              onClick={this.submitAdd}
              variant="contained"
              color="primary"
              size="small"
              disabled={this.state.adding || !this.state.addCode || !this.state.addExpiresAt}
            >
              {this.state.adding ? <CircularProgress size={18} /> : labels.save}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Upload dialog — cleaner, modern layout */}
        <Dialog
          open={this.state.uploadDialogOpen}
          onClose={this.closeUploadDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{ className: 'benefit-upload__paper' }}
        >
          <DialogTitle disableTypography className="benefit-upload__title">
            <div>
              <Typography component="h2" className="benefit-upload__title-text">{labels.upload_title}</Typography>
              <Typography component="p" className="benefit-upload__subtitle">{labels.upload_description}</Typography>
            </div>
            <IconButton size="small" onClick={this.closeUploadDialog} className="benefit-upload__close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent className="benefit-upload__body">
            <label
              className={`benefit-upload__dropzone${
                this.state.uploadFile ? ' benefit-upload__dropzone--has-file' : ''
              }${this.state.dragActive ? ' benefit-upload__dropzone--drag' : ''}`}
              onDrop={this.handleDrop}
              onDragOver={this.handleDragOver}
              onDragLeave={this.handleDragLeave}
            >
              <input hidden type="file" accept=".csv,.xls,.xlsx" onChange={this.handleFileChange} />
              {this.state.uploadFile ? (
                <div className="benefit-upload__file">
                  <InsertDriveFileOutlinedIcon className="benefit-upload__file-icon" />
                  <div className="benefit-upload__file-meta">
                    <div className="benefit-upload__file-name">{this.state.uploadFile.name}</div>
                    <div className="benefit-upload__file-size">{formatBytes(this.state.uploadFile.size)}</div>
                  </div>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.preventDefault(); this.clearSelectedFile(); }}
                    aria-label="Remove file"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              ) : (
                <div className="benefit-upload__dropzone-empty">
                  <CloudUploadIcon className="benefit-upload__dropzone-icon" />
                  <div className="benefit-upload__dropzone-title">{labels.upload_choose_file}</div>
                  <div className="benefit-upload__dropzone-hint">CSV &middot; XLS &middot; XLSX</div>
                </div>
              )}
            </label>

            {uploadData && (
              <div className="benefit-upload__result">
                <div className="benefit-upload__result-title">{labels.upload_result_title}</div>
                <div className="benefit-upload__stats">
                  <div className="benefit-upload__stat benefit-upload__stat--imported">
                    <CheckCircleOutlineIcon fontSize="small" />
                    <div>
                      <strong>{uploadData.imported ?? 0}</strong>
                      <span>{labels.upload_result_imported.replace('%{count}', '').trim()}</span>
                    </div>
                  </div>
                  <div className="benefit-upload__stat benefit-upload__stat--skipped">
                    <RadioButtonUncheckedIcon fontSize="small" />
                    <div>
                      <strong>{uploadData.skipped ?? 0}</strong>
                      <span>{labels.upload_result_skipped.replace('%{count}', '').trim()}</span>
                    </div>
                  </div>
                </div>
                {Array.isArray(uploadData.errors) && uploadData.errors.length > 0 && (
                  <div className="benefit-upload__errors">
                    <div className="benefit-upload__errors-title">{labels.upload_result_errors}</div>
                    <ul>
                      {uploadData.errors.map((err, idx) => (
                        <li key={idx}>Line {err.line}: {err.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {uploadResult && uploadResult.success === false && (
              <div className="benefit-upload__banner">
                <ErrorOutlineIcon fontSize="small" />
                <span>
                  {(Array.isArray(uploadResult.errors)
                    ? uploadResult.errors
                    : [uploadResult.errors]
                  ).filter(Boolean).map((err) => (typeof err === 'string' ? err : (err.message || JSON.stringify(err)))).join(', ')}
                </span>
              </div>
            )}
          </DialogContent>
          <DialogActions className="benefit-upload__footer">
            <Button onClick={this.closeUploadDialog} size="small">{labels.cancel}</Button>
            <Button
              onClick={this.submitUpload}
              variant="contained"
              color="primary"
              size="small"
              disabled={this.state.uploading || !this.state.uploadFile}
            >
              {this.state.uploading ? <CircularProgress size={18} /> : labels.upload_submit}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={this.state.snackbarOpen}
          autoHideDuration={4000}
          onClose={this.closeSnackbar}
          message={this.state.snackbarMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </MuiThemeProvider>
    );
  }
}
